import { useEffect, useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import PlanBreakdownSections, { type ChannelSectionData, type QuarterlySectionData } from './PlanBreakdownSections'
import RowCommentThread from './RowCommentThread'
import CellCommentTabs from './CellCommentTabs'
import OrphanCommentsBlock from './OrphanCommentsBlock'
import { rowKey, type RowAnchor } from './rowAnchor'
import { cellKey, cellLabel } from './cellAnchor'
import { groupByRow, countByCell, tabsByCell, newestTab, type CellTab, type CellLink } from './reviewComments'
import { type EditorRequest, type RequestComment } from './revisionRequest'
import { useRequestComments } from './useRequestComments'
import { type RevisionBaseline } from './DiffBits'
import { type PlanNodeFromServer } from './VersionViewOverallSection'
import { plan_top_down_review_comments_path, plan_top_down_review_comment_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'

type PeriodLabels = Parameters<typeof PlanBreakdownSections>[0]['periodLabels']

const noop = () => {}

// The review's Plan changes tab: the reviewed plan with diff marks, the
// manager's request comments (read-only) and the review's cell comments under
// each row, one tab per commented cell. A row with comments opens on the cell
// with the newest one; clicking a planned number opens that cell's tab. A
// finished review (readOnly) only shows what was said. `focus` (a cell name
// clicked on the Conversation tab) opens that cell's tab and scrolls to its row.
export default function ReviewPlanChangesTab({
  slug, taskId, planNodes, channelSection, quarterlySection, currency, periodLabels, baseline,
  revisionRequest, cellComments, focus, readOnly,
}: {
  slug: string
  taskId: string
  planNodes: PlanNodeFromServer[]
  channelSection: ChannelSectionData
  quarterlySection: QuarterlySectionData
  currency: string | null
  periodLabels: PeriodLabels
  baseline: RevisionBaseline | null
  revisionRequest: EditorRequest | null
  cellComments: RequestComment[]
  focus: (CellLink & { at: number }) | null
  readOnly: boolean
}) {
  const market = useMarket()
  // Per row: the cell whose tab is open (null = closed). A row left out
  // opens on its newest comment.
  const [picked, setPicked] = useState<Record<string, Picked | null>>({})
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const request = useRequestComments(revisionRequest?.comments ?? [])
  const reviewByRow = useMemo(() => groupByRow(cellComments), [cellComments])
  const perCell = useMemo(() => countByCell(cellComments), [cellComments])
  const reviewOrphans = useMemo(() => cellComments.filter((c) => !c.row_id), [cellComments])

  const rowState = (key: string) => {
    const tabs = tabsByCell(reviewByRow.get(key) ?? [])
    const pick = picked[key]
    if (pick && !tabs.some((t) => t.column_key === pick.column_key)) tabs.push({ ...pick, comments: [] })
    const active = pick === undefined ? (newestTab(tabs)?.column_key ?? null) : (pick?.column_key ?? null)
    return { tabs, active }
  }
  const pick = (key: string, value: Picked | null) => setPicked((p) => ({ ...p, [key]: value }))

  useEffect(() => {
    if (!focus) return
    pick(focus.row_key, { column_key: focus.column_key, label: focus.label })
    // The strip sits in the table row right under the plan row it belongs to.
    const frame = requestAnimationFrame(() => {
      const strip = document.querySelector(`[data-comment-row="${focus.row_key}"]`)
      strip?.closest('tr')?.previousElementSibling?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
    return () => cancelAnimationFrame(frame)
  }, [focus])

  const create = (anchor: RowAnchor, columnKey: string, body: string) => {
    const draftKey = cellKey({ ...anchor, column_key: columnKey })
    router.post(plan_top_down_review_comments_path(market.slug, slug, taskId), {
      section: anchor.section, anchor_id: anchor.anchor_id, column_key: columnKey, body,
    }, {
      preserveScroll: true,
      onSuccess: (page) => {
        // A refusal comes back as a redirect with an alert; keep the typed text then.
        const flash = page.props.flash as Record<string, string> | undefined
        if (!flash?.alert) setDrafts((d) => ({ ...d, [draftKey]: '' }))
      },
    })
  }
  const update = (id: string, body: string) =>
    router.patch(plan_top_down_review_comment_path(market.slug, slug, taskId, id), { body }, { preserveScroll: true })
  const destroy = (id: string) =>
    router.delete(plan_top_down_review_comment_path(market.slug, slug, taskId, id), { preserveScroll: true })

  return (
    <>
      {!readOnly && (
        <div className="mb-3 rounded-lg border border-border bg-card px-4 py-2.5 text-sm text-foreground">
          Click any planned number to add a comment on it.
        </div>
      )}
      <PlanBreakdownSections
        planNodes={planNodes}
        channelSection={channelSection}
        quarterlySection={quarterlySection}
        currency={currency}
        periodLabels={periodLabels}
        baseline={baseline}
        onCellAction={readOnly ? undefined : (cell) => {
          const key = rowKey(cell)
          pick(key, rowState(key).active === cell.column_key
            ? null
            : { column_key: cell.column_key, label: cellLabel(cell) })
        }}
        cellCommentCount={(cell) => perCell.get(cellKey(cell)) ?? 0}
        cellSelected={(cell) => rowState(rowKey(cell)).active === cell.column_key}
        rowExtras={(anchor) => {
          const key = rowKey(anchor)
          const requestList = request.byRow.get(key) ?? []
          const { tabs, active } = rowState(key)
          // A row with one commentable number shows its title instead of tabs.
          const withTabBar = anchor.section === 'channel'
            || (anchor.section === 'quarterly' && quarterlySection.plan_quarters.length > 1)
          const showReview = tabs.length > 0 && (withTabBar || active !== null)
          if (requestList.length === 0 && !showReview) return null
          const draftKey = active ? cellKey({ ...anchor, column_key: active }) : ''
          return (
            <div data-comment-row={key}>
              {requestList.length > 0 && (
                <RowCommentThread anchor={anchor} comments={requestList} composerOpen={false} readOnly
                  onOpenComposer={noop} onCloseComposer={noop} onCreate={noop} onUpdate={noop} onDelete={noop} />
              )}
              {showReview && (
                <CellCommentTabs
                  tabs={tabs}
                  active={active}
                  withTabBar={withTabBar}
                  readOnly={readOnly}
                  draft={drafts[draftKey] ?? ''}
                  onDraftChange={(body) => setDrafts((d) => ({ ...d, [draftKey]: body }))}
                  onSelect={(columnKey) => pick(key, tabFor(tabs, columnKey))}
                  onClose={() => pick(key, null)}
                  onCreate={(body) => active && create(anchor, active, body)}
                  onUpdate={update}
                  onDelete={destroy}
                />
              )}
            </div>
          )
        }}
      />
      <OrphanCommentsBlock comments={request.orphans} readOnly />
      <OrphanCommentsBlock comments={reviewOrphans} readOnly={readOnly} onDelete={destroy} />
    </>
  )
}

type Picked = { column_key: string; label: string }

function tabFor(tabs: CellTab[], columnKey: string): Picked | null {
  const tab = tabs.find((t) => t.column_key === columnKey)
  return tab ? { column_key: tab.column_key, label: tab.label } : null
}
